import { Badge, Button, ConfirmDialog, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, EmptyState, Input, Label, PageHeader, Select, SkeletonRows, StatusBadge, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Tabs, TabsList, TabsTrigger, saleStatusVariant, toast } from '@bia-auction/ui';
import { t } from '@bia-auction/shared-i18n';
import type { SaleDetail, SaleListBucket, SaleState, SaleSummary } from '@bia-auction/shared-schema';
import {
  bidAdminViewSchema,
  depositAdminViewSchema,
  SALE_STATES_BY_BUCKET,
  saleDetailSchema,
  saleSummarySchema,
} from '@bia-auction/shared-schema';
import { paginatedSchema } from '@bia-auction/shared-schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { Link } from '@tanstack/react-router';
import { Download, Gavel, MoreHorizontal, Plus, Search, XCircle } from 'lucide-react';
import { useState } from 'react';
import { ErrorMsg } from '../components/Feedback';
import { Pagination } from '../components/Pagination';
import { api, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatDate } from '../lib/format';
import { presentationLabel, saleStateLabel, saleStyleLabel } from '../lib/labels';
import { can } from '../lib/roles';
import { useDebounced } from '../lib/useDebounced';

type Tab = SaleListBucket;

const PAGE = 25;
const salesPageSchema = paginatedSchema(saleSummarySchema);

/** `yyyy-mm-dd` (native date input) → the inclusive epoch-ms bound for that local day. */
function dayBound(value: string, edge: 'start' | 'end'): string | null {
  if (!value) return null;
  const ms = new Date(`${value}${edge === 'start' ? 'T00:00:00' : 'T23:59:59.999'}`).getTime();
  return Number.isNaN(ms) ? null : String(ms); // a half-typed date ("2026-0") must not send NaN
}

export function SalesListPage() {
  const { state } = useAuth();
  const [tab, setTab] = useState<Tab>('future');
  const [search, setSearch] = useState('');
  // '' = every state the tab covers. The options are the tab's own states (SALE_STATES_BY_BUCKET)
  // because the two filters AND server-side — an out-of-tab status would just empty the table.
  const [status, setStatus] = useState<SaleState | ''>('');
  const [from, setFrom] = useState(''); // yyyy-mm-dd; converted to epoch-ms bounds below
  const [to, setTo] = useState('');
  const [offset, setOffset] = useState(0);
  const [toCancel, setToCancel] = useState<SaleSummary | null>(null);
  const q = useDebounced(search.trim());
  const qc = useQueryClient();

  const tabStates = SALE_STATES_BY_BUCKET[tab];
  const filtered = status !== '' || from !== '' || to !== '';

  // Server-side search + pagination (M10): `q` LIKEs over code/title/location, `limit` opts
  // into the { items, total } envelope. The status + start-date filters (this pass) are
  // likewise server-side, so they narrow `total` and the paging with it — filtering only the
  // 25 rows already on screen would have left the pager counting rows the operator excluded.
  const params = new URLSearchParams({ state: tab, limit: String(PAGE), offset: String(offset) });
  if (q) params.set('q', q);
  if (status) params.set('status', status);
  const fromMs = dayBound(from, 'start');
  const toMs = dayBound(to, 'end');
  if (fromMs) params.set('startFrom', fromMs);
  if (toMs) params.set('startTo', toMs);
  const qs = params.toString();

  const list = useQuery({
    queryKey: ['sales', qs],
    queryFn: () => api(`/admin/sales?${qs}`, {}, salesPageSchema),
  });

  const resetFilters = () => {
    setStatus('');
    setFrom('');
    setTo('');
    setOffset(0);
  };

  const cancel = useMutation({
    mutationFn: (id: string) => api<void>(`/admin/sales/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setToCancel(null);
      toast(t('sales.cancelled'), 'success');
      void qc.invalidateQueries({ queryKey: ['sales'] });
    },
    onError: (e) => {
      setToCancel(null);
      toast(e instanceof ApiError ? e.message : t('sales.cancelFailed'), 'error');
    },
  });

  // Per-row XLSX export (M6 deliverable): fetch the sale detail + deposits/bids and build the
  // workbook client-side (same as the live console), lazy-loading SheetJS.
  const onExport = async (saleId: string) => {
    try {
      const [detail, deposits, bids] = await Promise.all([
        api(`/admin/sales/${saleId}`, {}, saleDetailSchema) as Promise<SaleDetail>,
        can(state.roles, 'depositView')
          ? api(`/admin/sales/${saleId}/deposits`, {}, z.array(depositAdminViewSchema))
          : Promise.resolve([]),
        api(`/admin/sales/${saleId}/bids`, {}, z.array(bidAdminViewSchema)),
      ]);
      const { exportSaleXlsx } = await import('../lib/xlsx');
      exportSaleXlsx(detail, deposits, bids);
    } catch (e) {
      toast(e instanceof ApiError ? e.message : t('saleDetail.exportFailed'), 'error');
    }
  };

  const newSaleButton = can(state.roles, 'salesCreate') && (
    <Button asChild size="sm">
      <Link to="/sales/new">
        <Plus className="h-4 w-4" /> {t('sales.new')}
      </Link>
    </Button>
  );

  return (
    <div className="space-y-4">
      <PageHeader title={t('sales.title')}>{newSaleButton}</PageHeader>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <Tabs
          value={tab}
          onValueChange={(v) => {
            setTab(v as Tab);
            // The status options belong to the tab; keeping a stale one (e.g. LIVE while
            // switching to "Geçmiş") would show an empty table with no visible cause.
            resetFilters();
          }}
        >
          <TabsList>
            <TabsTrigger value="future">{t('sales.tab.future')}</TabsTrigger>
            <TabsTrigger value="past">{t('sales.tab.past')}</TabsTrigger>
            <TabsTrigger value="draft">{t('sales.tab.draft')}</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative w-64">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-8"
            placeholder={t('sales.search.placeholder')}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setOffset(0); // a new search restarts paging
            }}
          />
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {/* A single-state tab (Taslak) has nothing to narrow — the dropdown would offer one
            option that changes nothing. */}
        {tabStates.length > 1 && (
          <div className="space-y-1">
            <Label htmlFor="f-sale-status">{t('sales.filter.status')}</Label>
            <Select
              id="f-sale-status"
              className="w-48"
              value={status}
              onChange={(e) => {
                setStatus(e.target.value as SaleState | '');
                setOffset(0); // a filter change restarts paging
              }}
            >
              <option value="">{t('common.all')}</option>
              {tabStates.map((s) => (
                <option key={s} value={s}>
                  {saleStateLabel(s)}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div className="space-y-1">
          <Label htmlFor="f-sale-from">{t('sales.filter.from')}</Label>
          <Input
            id="f-sale-from"
            type="date"
            className="w-44"
            value={from}
            onChange={(e) => {
              setFrom(e.target.value);
              setOffset(0);
            }}
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="f-sale-to">{t('sales.filter.to')}</Label>
          <Input
            id="f-sale-to"
            type="date"
            className="w-44"
            value={to}
            onChange={(e) => {
              setTo(e.target.value);
              setOffset(0);
            }}
          />
        </div>
        {filtered && (
          <Button variant="ghost" size="sm" onClick={resetFilters}>
            <XCircle className="h-4 w-4" /> {t('sales.filter.clear')}
          </Button>
        )}
      </div>

      {list.isLoading ? (
        <SkeletonRows rows={6} />
      ) : list.isError ? (
        <ErrorMsg error={list.error} />
      ) : list.data && list.data.items.length > 0 ? (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('sales.col.code')}</TableHead>
              <TableHead>{t('sales.col.title')}</TableHead>
              <TableHead>{t('sales.col.style')}</TableHead>
              <TableHead>{t('sales.col.start')}</TableHead>
              <TableHead>{t('sales.col.lots')}</TableHead>
              <TableHead>{t('sales.col.status')}</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data.items.map((s) => {
              const cancellable =
                can(state.roles, 'salesDelete') && s.state !== 'CLOSED' && s.state !== 'CANCELLED';
              const exportable = can(state.roles, 'exportSale');
              return (
                <TableRow key={s.id}>
                  <TableCell className="font-mono text-xs">{s.code}</TableCell>
                  <TableCell>
                    {can(state.roles, 'salesView') ? (
                      <Link to="/sales/$saleId" params={{ saleId: s.id }} className="text-tx-brand hover:underline">
                        {s.title ?? t('sales.untitled')}
                      </Link>
                    ) : (
                      <span>{s.title ?? t('sales.untitled')}</span>
                    )}
                    {/* The location under the name: with the code in its own column, the name
                        column was the only place an operator could tell two same-day auctions
                        apart, and the title alone often doesn't. */}
                    {s.location && (
                      <span className="block text-xs text-muted-foreground">{s.location}</span>
                    )}
                  </TableCell>
                  <TableCell>{saleStyleLabel(s.style)}</TableCell>
                  <TableCell className="tabular-nums">{formatDate(s.startDate)}</TableCell>
                  <TableCell className="tabular-nums">{s.lotCount}</TableCell>
                  <TableCell>
                    {/* Admin sees the raw state → the canonical docs/15 §6 mapping; the
                        label stays the masked presentation wording users know. */}
                    <StatusBadge variant={saleStatusVariant(s.state)}>
                      {presentationLabel(s.presentationStatus)}
                    </StatusBadge>
                  </TableCell>
                  <TableCell className="text-right">
                    {(exportable || cancellable) && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" aria-label={t('common.actions')}>
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {exportable && (
                            <DropdownMenuItem onSelect={() => void onExport(s.id)}>
                              <Download className="h-4 w-4" /> {t('saleDetail.export')}
                            </DropdownMenuItem>
                          )}
                          {cancellable && (
                            <DropdownMenuItem destructive onSelect={() => setToCancel(s)}>
                              <XCircle className="h-4 w-4" /> {t('sales.cancelTitle')}
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      ) : (
        // "No auctions in this tab" is wrong — and misleading — when a filter is what emptied
        // it; the way out is then the filters, not creating an auction.
        <EmptyState
          icon={Gavel}
          title={filtered ? t('sales.emptyFiltered') : t('sales.empty')}
          action={
            filtered ? (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                {t('sales.filter.clear')}
              </Button>
            ) : (
              newSaleButton || undefined
            )
          }
        />
      )}
      {list.data && (
        <Pagination offset={offset} limit={PAGE} total={list.data.total} onOffsetChange={setOffset} />
      )}

      <ConfirmDialog
        open={toCancel !== null}
        onOpenChange={(open) => !open && setToCancel(null)}
        title={t('sales.cancelTitle')}
        description={toCancel ? t('sales.cancelConfirm', { name: toCancel.title ?? toCancel.code }) : ''}
        confirmLabel={t('sales.cancelTitle')}
        cancelLabel={t('common.cancel')}
        destructive
        busy={cancel.isPending}
        onConfirm={() => toCancel && cancel.mutate(toCancel.id)}
      />
    </div>
  );
}
